"use client";

import { Globe, Headset, Trophy, Users } from "lucide-react";
import { memo } from "react";
import CountUp from "react-countup";

const STATS = [
  {
    label: "Happy travelers",
    description:
      "People who have already planned flights, stays, or trips with us.",
    icon: Users,
    value: 4,
    suffix: "K",
  },
  {
    label: "Destination networks",
    description:
      "Coverage across routes, stays, and curated travel planning options.",
    icon: Globe,
    value: 30,
    suffix: "+",
  },
  {
    label: "Curated trip ideas",
    description:
      "Holiday and discovery combinations surfaced for faster decisions.",
    icon: Trophy,
    value: 40,
    suffix: "",
  },
  {
    label: "Support availability",
    description:
      "A service desk that stays available when plans need to change.",
    icon: Headset,
    value: 24,
    suffix: "h",
  },
];

const StatItem = memo(({ stat }: { stat: (typeof STATS)[0] }) => {
  const Icon = stat.icon;

  return (
    <div className="group border-t border-slate-200/80 pt-4 first:border-t-0 first:pt-0 dark:border-slate-800/80 sm:border-t-0 sm:pt-0 sm:border-l sm:first:border-l-0 sm:border-slate-200/80 sm:pl-4 sm:first:pl-0 dark:sm:border-slate-800/80 xl:pl-6">
      <div className="flex items-start justify-between gap-4">
        <div className="grid size-11 place-items-center rounded-2xl bg-primary text-primary-foreground">
          <Icon className="h-5 w-5 text-white" strokeWidth={2} />
        </div>
        <span className="text-4xl font-black tracking-tight text-foreground/20 transition-opacity duration-300 group-hover:opacity-50">
          <CountUp
            end={stat.value}
            suffix={stat.suffix}
            duration={3.2}
            useEasing
            enableScrollSpy
            scrollSpyOnce
          />
        </span>
      </div>

      <div className="mt-5">
        <span className="text-sm font-semibold text-foreground md:text-base">
          {stat.label}
        </span>
        <p className="mt-2 max-w-[26ch] text-sm leading-6 text-muted-foreground">
          {stat.description}
        </p>
      </div>
    </div>
  );
});

StatItem.displayName = "StatItem";

export function StatsSection() {
  return (
    <section className="relative ">
      <div className="pointer-events-none absolute -right-16 -top-12 h-44 w-44 rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-10 left-8 h-32 w-32 rounded-full bg-primary/8 blur-3xl" />

      <div className="relative">
        <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {STATS.map((stat) => (
            <StatItem key={stat.label} stat={stat} />
          ))}
        </div>
      </div>
    </section>
  );
}
